import { useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { LOGO_HEIGHT, LOGO_MAX_ASPECT, LOGO_MIN_ASPECT, type PracticeLogo } from '@dermalink/shared';
import { PracticeMark } from '../../components/AppHeader.tsx';
import styles from './LogoCropper.module.css';

const SHAPES: { aspect: number; label: string }[] = [
  { aspect: 1, label: 'Vierkant' },
  { aspect: 2, label: '2 : 1' },
  { aspect: 3, label: '3 : 1' },
  { aspect: 4, label: 'Breed 4 : 1' },
];

// Below 1 the frame can be larger than the logo, so a whole logo fits with transparent margins.
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;

// Mean luminance of the visible pixels above which a logo disappears on the white header.
const TOO_LIGHT = 0.88;

interface LogoCropperProps {
  file:        File;
  practiceName: string;
  busy:        boolean;
  onCancel:    () => void;
  onConfirm:   (png: Blob) => void;
}

/**
 * Lets an admin frame their logo: pick the frame's proportions (a square symbol up
 * to a wide wordmark), then pan and zoom the image inside it. The frame is
 * rendered to a transparent PNG of LOGO_HEIGHT px high — exactly what the server
 * accepts — and previewed live in the patient header.
 */
export function LogoCropper({ file, practiceName, busy, onCancel, onConfirm }: LogoCropperProps) {
  const [image, setImage]   = useState<HTMLImageElement | null>(null);
  const [aspect, setAspect] = useState(1);
  const [crop, setCrop]     = useState({ x: 0, y: 0 });
  const [zoom, setZoom]     = useState(1);
  const [area, setArea]     = useState<Area | null>(null);
  const [preview, setPreview] = useState<{ logo: PracticeLogo; blob: Blob; tooLight: boolean } | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // Revoking the URL fails a load still in flight; a stale load (e.g. StrictMode's
    // first effect run) must not report that as the file being unreadable.
    let cancelled = false;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      setImage(img);
      setAspect(nearestShape(img.naturalWidth / img.naturalHeight));
    };
    img.onerror = () => { if (!cancelled) setLoadError(true); };
    img.src = url;
    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
    };
  }, [file]);

  useEffect(() => {
    if (!image || !area) return;
    let cancelled = false;
    void renderLogo(image, area, aspect).then(({ blob, tooLight }) => {
      if (cancelled) return;
      setPreview({ logo: { url: URL.createObjectURL(blob), width: Math.round(LOGO_HEIGHT * aspect), height: LOGO_HEIGHT }, blob, tooLight });
    });
    return () => { cancelled = true; };
  }, [image, area, aspect]);

  // Revoke each preview's object URL only once the next one has replaced it, so the preview never flickers.
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.logo.url); }, [preview]);

  if (loadError) {
    return (
      <div className={styles.editor}>
        <p className={styles.error}>Deze afbeelding kon niet worden geopend.</p>
        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={onCancel}>Andere afbeelding kiezen</button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      <div className={styles.shapes} role="radiogroup" aria-label="Vorm van het logo">
        {SHAPES.map((shape) => (
          <button key={shape.aspect} type="button" role="radio" aria-checked={aspect === shape.aspect} className={`${styles.shape}${aspect === shape.aspect ? ` ${styles.shapeSelected}` : ''}`} onClick={() => setAspect(shape.aspect)}>
            <span className={styles.shapeIcon} style={{ aspectRatio: shape.aspect }} />
            {shape.label}
          </button>
        ))}
      </div>

      <div className={styles.stage}>
        {image && (
          <Cropper
            image={image.src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            restrictPosition={false}
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={(_, pixels) => setArea(pixels)}
            classes={{ containerClassName: styles.cropContainer, cropAreaClassName: styles.cropArea }}
          />
        )}
      </div>

      <label className={styles.zoom}>
        <span>Zoom</span>
        <input type="range" min={MIN_ZOOM} max={MAX_ZOOM} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
      </label>
      <p className={styles.hint}>Sleep het logo in het kader en zoom tot het goed past. Uitzoomen laat ruimte rond het logo.</p>

      <p className={styles.previewLabel}>Zo zien patiënten het</p>
      <div className={styles.previewHeader}>
        {preview && <PracticeMark practice={{ name: practiceName, logo: preview.logo }} />}
      </div>
      {preview?.tooLight && <p className={styles.warning}>Dit logo is erg licht en valt weg op de witte balk. Gebruik bij voorkeur een donkere of gekleurde versie.</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.button} onClick={onCancel} disabled={busy}>Annuleren</button>
        <button type="button" className={`${styles.button} ${styles.buttonPrimary}`} onClick={() => preview && onConfirm(preview.blob)} disabled={busy || !preview}>
          {busy ? 'Opslaan…' : 'Logo opslaan'}
        </button>
      </div>
    </div>
  );
}

function nearestShape(imageAspect: number): number {
  const clamped = Math.min(LOGO_MAX_ASPECT, Math.max(LOGO_MIN_ASPECT, imageAspect));
  return SHAPES.reduce((best, s) => Math.abs(s.aspect - clamped) < Math.abs(best.aspect - clamped) ? s : best).aspect;
}

/** Draws the framed part of the image onto a transparent LOGO_HEIGHT-high canvas. `area` may extend past the image when zoomed out. */
async function renderLogo(image: HTMLImageElement, area: Area, aspect: number): Promise<{ blob: Blob; tooLight: boolean }> {
  const canvas = document.createElement('canvas');
  canvas.height = LOGO_HEIGHT;
  canvas.width  = Math.round(LOGO_HEIGHT * aspect);
  const ctx   = canvas.getContext('2d')!;
  const scale = canvas.height / area.height;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, -area.x * scale, -area.y * scale, image.naturalWidth * scale, image.naturalHeight * scale);

  const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas export failed')), 'image/png'));
  return { blob, tooLight: meanVisibleLuminance(ctx.getImageData(0, 0, canvas.width, canvas.height).data) > TOO_LIGHT };
}

function meanVisibleLuminance(rgba: Uint8ClampedArray): number {
  let sum = 0;
  let weight = 0;
  for (let i = 0; i < rgba.length; i += 4) {
    const alpha = rgba[i + 3] / 255;
    sum    += alpha * (0.2126 * rgba[i] + 0.7152 * rgba[i + 1] + 0.0722 * rgba[i + 2]) / 255;
    weight += alpha;
  }
  return weight ? sum / weight : 1;
}
